import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fridaveAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

function getPosSuffix(pos) {
  if (!pos) return '';
  const t = pos % 100, l = pos % 10;
  if (t >= 11 && t <= 13) return 'th';
  if (l === 1) return 'st';
  if (l === 2) return 'nd';
  if (l === 3) return 'rd';
  return 'th';
}

export default function FriDaveSeriesPlayerDetail() {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const currentYear = String(new Date().getFullYear());

  const [rounds, setRounds] = useState([]);
  const [playerName, setPlayerName] = useState('');
  const [seriesPoints, setSeriesPoints] = useState(0);
  const [position, setPosition] = useState(null);
  const [isTied, setIsTied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [playerRounds, standings] = await Promise.all([
          fridaveAPI.getPlayerRounds(playerId, currentYear),
          fridaveAPI.getStandings(currentYear).catch(() => []),
        ]);
        if (cancelled) return;

        const roundsList = Array.isArray(playerRounds) ? playerRounds : [];
        setRounds(roundsList);
        if (roundsList[0]?.player_name) setPlayerName(roundsList[0].player_name);

        const list = Array.isArray(standings) ? standings : [];
        const withPos = [];
        list.forEach((player, index) => {
          const pos = index === 0 ? 1 : (player.series_points === list[index - 1].series_points ? withPos[index - 1].pos : index + 1);
          const tied = list.some((other, i) => i !== index && other.series_points === player.series_points);
          withPos.push({ ...player, pos, isTied: tied });
        });
        const mine = withPos.find(p => String(p.player_id) === String(playerId));
        if (mine) {
          setPosition(mine.pos);
          setIsTied(mine.isTied);
          setSeriesPoints(mine.series_points);
          if (!roundsList[0]?.player_name) setPlayerName(mine.player_name);
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load player rounds');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [playerId, currentYear]);

  if (loading) return <div className="flex justify-center py-12"><LoadingSpinner /></div>;

  return (
    <div className="px-4 pb-4">
      <button onClick={() => navigate(-1)} className="text-fd-primary font-semibold flex items-center gap-1 mb-4">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Standings
      </button>

      {error ? (
        <div className="bg-white rounded-2xl p-8 text-center border border-gray-200">
          <p className="text-gray-500 text-sm">{error}</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-2xl p-5 mb-4 text-center border border-gray-200 shadow-sm">
            <h2 className="text-xl font-bold text-gray-900">{playerName}</h2>
            {position && (
              <p className="text-gray-400 text-sm mt-1">
                {isTied ? 'T' : ''}{position}{getPosSuffix(position)} &middot; Series Standings
              </p>
            )}
            <p className="text-4xl font-extrabold text-gray-900 mt-3">{seriesPoints}</p>
            <p className="text-gray-400 text-xs mt-1">points &middot; best 3 of {rounds.length} round{rounds.length !== 1 ? 's' : ''}</p>
          </div>

          <div className="px-1 pb-2 text-[11px] font-bold text-gray-400 uppercase tracking-wide">
            Contributing Rounds &middot; Highest to Lowest
          </div>

          {rounds.length > 0 ? (
            <div className="space-y-2">
              {rounds.map((r) => (
                <button
                  key={r.round_id}
                  onClick={() => navigate(`/fridave/rounds/${r.round_id}/players/${playerId}`)}
                  className={`w-full flex items-center rounded-2xl px-4 py-3 text-left transition-colors ${
                    r.counts
                      ? 'bg-green-50 border border-fd-gold-light/40 shadow-sm'
                      : 'bg-white border border-gray-200 opacity-60 hover:opacity-100'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{r.event_name}</p>
                    <p className="text-gray-400 text-xs mt-0.5">
                      {new Date(r.start_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`font-bold text-lg ${r.counts ? 'text-gray-900' : 'text-gray-400'}`}>{r.total_points}</p>
                    {!r.counts && <p className="text-[10px] text-gray-400">not counted</p>}
                  </div>
                  <svg className="w-4 h-4 text-gray-300 shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-8 text-center border border-gray-200">
              <p className="text-gray-500 text-sm">No completed rounds yet</p>
            </div>
          )}

          <p className="text-center text-xs text-gray-400 mt-4">
            Highlighted rounds count toward the series total &middot; tap a round to view its scorecard
          </p>
        </>
      )}
    </div>
  );
}
